import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { partyApi } from '../../services/api.party';
import config from '../../config/environment';


const EditCandidateModal = ({ candidate, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [constituencies, setConstituencies] = useState([]);
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [customSymbolFile, setCustomSymbolFile] = useState(null);
  const [customSymbolPreview, setCustomSymbolPreview] = useState('');
  // ✅ NEW: Affidavit PDF file state
  const [affidavitFile, setAffidavitFile] = useState(null);
  const [affidavitPreview, setAffidavitPreview] = useState('');
  
  const [formData, setFormData] = useState({
    constituencyCode: candidate.constituencyCode || '',
    candidateName: candidate.name || candidate.candidateName || '',
    partyShortName: candidate.partyShortName || '',
    partyName: candidate.partyName || '',
    age: candidate.age || '',
    education: candidate.education || '',
    profession: candidate.profession || '',
    address: candidate.address || '',
    backgroundSourceUrl: candidate.backgroundSourceUrl || '',
    assets: candidate.assets || '',
    liabilities: candidate.liabilities || '',
    criminalCases: (candidate.criminalCases ?? '0'),
    email: candidate.email || '',
    mobile: candidate.mobile || '',
    // ✅ NEW: Affidavit PDF URL field
    affidavitPdfUrl: candidate.affidavitPdfUrl || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);


  useEffect(() => {
    loadConstituencies();
    loadParties();
    
    // Set initial photo preview
    if (candidate.photoUrl) {
      setPhotoPreview(getFullImageUrl(candidate.photoUrl));
    }
    
    // Set initial custom symbol preview if exists
    if (candidate.customSymbolUrl) {
      setCustomSymbolPreview(getFullImageUrl(candidate.customSymbolUrl));
    }

    // ✅ NEW: Set initial affidavit preview if exists
    if (candidate.affidavitPdfUrl) {
      setAffidavitPreview(candidate.affidavitPdfUrl);
    }
  }, []);


  const loadConstituencies = async () => {
    try {
      const data = await api.getConstituencies();
      setConstituencies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading constituencies:', error);
    }
  };


  const loadParties = async () => {
    try {
      const data = await partyApi.list({ pageSize: 200 });
      const partyList = data.items || [];
      setParties(partyList);
      
      // Find and set the current party
      const currentParty = partyList.find(p => p.shortName === candidate.partyShortName);
      if (currentParty) {
        setSelectedParty(currentParty);
      }
    } catch (error) {
      console.error('Error loading parties:', error);
      setParties([]);
    }
  };


  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
      return url;
    }
    const baseUrl = config.API_URL.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };


  const handlePartyChange = (e) => {
    const shortName = e.target.value;
    const party = parties.find(p => p.shortName === shortName);
    
    setSelectedParty(party || null);
    setCustomSymbolFile(null);
    setCustomSymbolPreview(candidate.customSymbolUrl ? getFullImageUrl(candidate.customSymbolUrl) : '');
    
    if (party) {
      setFormData(prev => ({
        ...prev,
        partyShortName: party.shortName || '',
        partyName: party.name || ''
      }));
    } else if (shortName === 'IND') {
      setFormData(prev => ({
        ...prev,
        partyShortName: 'IND',
        partyName: 'Independent'
      }));
    } else if (shortName === 'OTHER') {
      setFormData(prev => ({
        ...prev,
        partyShortName: 'OTHER',
        partyName: 'Other'
      }));
    } else if (shortName === 'NOTA') {
      setFormData(prev => ({
        ...prev,
        partyShortName: 'NOTA',
        partyName: 'None of the Above'
      }));
    }
  };


  const handleCustomSymbolChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;


    try {
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('Symbol size must be less than 2MB');
      }


      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only PNG, JPG, JPEG, GIF, SVG allowed');
      }


      const previewUrl = URL.createObjectURL(file);
      setCustomSymbolPreview(previewUrl);
      setCustomSymbolFile(file);
      
      console.log('✅ Custom symbol file selected:', file.name);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  // ✅ NEW: Handle Affidavit PDF Upload
  const handleAffidavitChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('PDF size must be less than 10MB');
      }

      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }

      setAffidavitFile(file);
      setAffidavitPreview(file.name);
      
      console.log('✅ Affidavit PDF file selected:', file.name);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  // ✅ NEW: Remove Affidavit PDF
  const removeAffidavit = () => {
    setAffidavitFile(null);
    setAffidavitPreview(candidate.affidavitPdfUrl || '');
  };


  const handleChange = (e) => {
    const { name, value } = e.target;


    if (name === 'mobile') {
      const digits = value.replace(/\D/g, '').slice(0, 15);
      setFormData(prev => ({ ...prev, mobile: digits }));
      return;
    }


    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setMessage({ type: 'error', text: 'Please select a valid image file (JPG, PNG, or GIF)' });
        return;
      }


      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
        return;
      }


      setPhotoFile(file);


      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
      
      console.log('✅ Photo file selected:', file.name);
    }
  };


  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(candidate.photoUrl ? getFullImageUrl(candidate.photoUrl) : null);
  };


  const validateEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };


  const validateMobile = (mobile) => {
    if (!mobile) return true;
    return /^\d{10,15}$/.test(mobile);
  };


  const uploadCustomSymbol = async () => {
    if (!customSymbolFile) return '';
    
    try {
      console.log('🎨 Uploading custom symbol...');
      const result = await partyApi.uploadSymbol(customSymbolFile);
      console.log('✅ Symbol uploaded successfully:', result.url);
      return result.url;
    } catch (error) {
      console.error('❌ Symbol upload error:', error);
      throw error;
    }
  };

  // ✅ NEW: Upload Affidavit PDF
  const uploadAffidavit = async () => {
    if (!affidavitFile) return '';
    
    try {
      console.log('📄 Uploading affidavit PDF...');
      const result = await api.uploadAffidavitPdf(affidavitFile);
      console.log('✅ Affidavit PDF uploaded successfully:', result.url);
      return result.url;
    } catch (error) {
      console.error('❌ Affidavit PDF upload error:', error);
      throw error;
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);


    if (!validateEmail(formData.email)) {
      setLoading(false);
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    if (!validateMobile(formData.mobile)) {
      setLoading(false);
      setMessage({ type: 'error', text: 'Please enter a valid mobile number (10-15 digits)' });
      return;
    }


    try {
      let photoUrl = candidate.photoUrl;
      let customSymbolUrl = candidate.customSymbolUrl || '';
      let affidavitPdfUrl = candidate.affidavitPdfUrl || ''; // ✅ NEW


      // ✅ Upload photo if new file selected
      if (photoFile) {
        console.log('📸 Uploading candidate photo...');
        const photoResult = await api.uploadCandidatePhoto(photoFile);
        photoUrl = photoResult.url || photoResult.photoUrl;
        console.log('✅ Photo uploaded:', photoUrl);
      }


      // ✅ Check if party needs custom symbol (IND, OTHER, NOTA)
      const needsCustomSymbol = 
        formData.partyShortName === 'IND' || 
        formData.partyShortName === 'OTHER' ||
        formData.partyShortName === 'NOTA';


      // ✅ Upload custom symbol if new file selected and party needs it
      if (customSymbolFile && needsCustomSymbol) {
        customSymbolUrl = await uploadCustomSymbol();
      }

      // ✅ NEW: Upload affidavit PDF if new file selected
      if (affidavitFile) {
        console.log('📄 Uploading affidavit PDF...');
        affidavitPdfUrl = await uploadAffidavit();
        console.log('✅ Affidavit PDF uploaded:', affidavitPdfUrl);
      }


      const updateData = {
        ...formData,
        photoUrl,
        customSymbol: customSymbolUrl,
        // ✅ NEW: Include affidavit PDF URL
        affidavitPdfUrl
      };


      console.log('📤 Updating candidate:', updateData);


      await api.updateCandidate(candidate.id, updateData);
      onSuccess();
    } catch (error) {
      console.error('❌ Update error:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to update candidate'
      });
    } finally {
      setLoading(false);
    }
  };


  const getPartySymbol = () => {
    if (customSymbolPreview) return customSymbolPreview;
    if (selectedParty?.symbolUrl) return getFullImageUrl(selectedParty.symbolUrl);
    if (candidate.symbolUrl) return getFullImageUrl(candidate.symbolUrl);
    return '';
  };


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <i className="fas fa-edit"></i>
            {t('candidate.editTitle') || 'Edit Candidate'}
          </h3>
          <button onClick={onClose} className="close-button">×</button>
        </div>


        {message && (
          <div className={`alert alert-${message.type}`}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
            {message.text}
            <button className="alert-close" onClick={() => setMessage(null)}>×</button>
          </div>
        )}


        <form onSubmit={handleSubmit} className="modal-body">
          {/* Photo Upload - Two-Button Version */}
          <div className="form-section">
            <h4><i className="fas fa-camera"></i> Photo</h4>
            <div className="photo-upload-containers">
              <input
                type="file"
                id="editPhotoUpload"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
              
              {photoPreview && (
                <div className="photo-preview-wrappers">
                  <img src={photoPreview} alt="Preview" className="photo-preview" />
                </div>
              )}
              
              <div className="photo-action-buttons">
                <label htmlFor="editPhotoUpload" className="btn-upload-photo">
                  <i className="fas fa-cloud-upload-alt"></i>
                  {photoPreview ? 'Change Photo' : 'Upload Photo'}
                </label>
                
                {photoPreview && photoFile && (
                  <button
                    type="button"
                    className="btn-remove-photo"
                    onClick={removePhoto}
                  >
                    <i className="fas fa-trash"></i>
                    Remove
                  </button>
                )}
              </div>
              
              <small className="photo-hint">JPG, PNG or GIF (Max 5MB)</small>
            </div>
          </div>



          {/* Constituency */}
          <div className="form-section">
            <h4><i className="fas fa-landmark"></i> Constituency</h4>
            <div className="form-group">
              <label htmlFor="constituencyCode">Constituency *</label>
              <select
                id="constituencyCode"
                name="constituencyCode"
                value={formData.constituencyCode}
                onChange={handleChange}
                required
              >
                <option value="">Select Constituency</option>
                {constituencies.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.code} - {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>


          {/* Personal Details */}
          <div className="form-section">
            <h4><i className="fas fa-user"></i> Personal Details</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="candidateName">Candidate Name *</label>
                <input
                  id="candidateName"
                  type="text"
                  name="candidateName"
                  value={formData.candidateName}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="age">Age</label>
                <input
                  id="age"
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="45"
                  min="25"
                  max="100"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="education">Education</label>
                <input
                  id="education"
                  type="text"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  placeholder="e.g., Graduate"
                />
              </div>
              <div className="form-group">
                <label htmlFor="profession">Profession</label>
                <input
                  id="profession"
                  type="text"
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  placeholder="e.g., Politician"
                />
              </div>
            </div>


            {/* Contact Details */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="candidate@example.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mobile">Mobile</label>
                <input
                  id="mobile"
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="10-15 digits"
                  inputMode="numeric"
                  pattern="\d*"
                />
                <small className="field-hint">Only digits, 10-15 length</small>
              </div>
            </div>


            <div className="form-group">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter full address"
                rows="2"
              />
            </div>
          </div>


          {/* Party Information with Auto-fill */}
          <div className="form-section party-section">
            <h4><i className="fas fa-flag"></i> Party Details</h4>
            
            <div className="party-selector-wrapper">
              <div className="form-group party-dropdown">
                <label htmlFor="partySelect">Select Party *</label>
                <select
                  id="partySelect"
                  value={selectedParty?.shortName || formData.partyShortName}
                  onChange={handlePartyChange}
                  required
                >
                  <option value="">Choose a party...</option>
                  <optgroup label="Major Parties">
                    {parties.filter(p => p.candidatesCount > 0).map(party => (
                      <option key={party.id} value={party.shortName}>
                        {party.shortName} - {party.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Other Parties">
                    {parties.filter(p => p.candidatesCount === 0).map(party => (
                      <option key={party.id} value={party.shortName}>
                        {party.shortName} - {party.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Special">
                    <option value="IND">IND - Independent</option>
                    <option value="NOTA">NOTA - None of the Above</option>
                    <option value="OTHER">OTHER - Enter Manually</option>
                  </optgroup>
                </select>
              </div>


              {/* Party Symbol Display */}
              {(selectedParty || formData.partyShortName) && (
                <div className="party-symbol-display">
                  <div className="symbol-preview-box">
                    {getPartySymbol() ? (
                      <img src={getPartySymbol()} alt="Party Symbol" />
                    ) : (
                      <div className="no-symbol">
                        <i className="fas fa-image"></i>
                        <p>No Symbol</p>
                      </div>
                    )}
                  </div>
                  <div className="symbol-info">
                    <p><strong>{formData.partyShortName}</strong></p>
                    <p className="party-full-name">{formData.partyName}</p>
                  </div>
                </div>
              )}
            </div>


            {/* Manual Party Entry for OTHER */}
            {formData.partyShortName === 'OTHER' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="partyShortName">Party Short Name *</label>
                  <input
                    id="partyShortName"
                    type="text"
                    name="partyShortName"
                    value={formData.partyShortName}
                    onChange={handleChange}
                    placeholder="BJP"
                    required
                    maxLength={10}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="partyName">Party Full Name *</label>
                  <input
                    id="partyName"
                    type="text"
                    name="partyName"
                    value={formData.partyName}
                    onChange={handleChange}
                    placeholder="Bharatiya Janata Party"
                    required
                  />
                </div>
              </div>
            )}


            {/* Custom Symbol Upload for Independent/Other/NOTA */}
            {(formData.partyShortName === 'IND' || 
              formData.partyShortName === 'OTHER' || 
              formData.partyShortName === 'NOTA') && (
              <div className="form-group custom-symbol-upload">
                <label>
                  <i className="fas fa-image"></i>
                  Upload Custom Symbol (Optional)
                </label>
                <div className="upload-box">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCustomSymbolChange}
                    id="customSymbolEdit"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="customSymbolEdit" className="upload-label">
                    <i className="fas fa-upload"></i>
                    <span>Choose Symbol Image</span>
                  </label>
                </div>
                <small>PNG, JPG, JPEG, GIF, SVG up to 2MB</small>
                
                {customSymbolPreview && (
                  <div className="symbol-preview-small" style={{marginTop: '12px', padding: '10px', background: '#f9fafb', borderRadius: '8px', textAlign: 'center'}}>
                    <img 
                      src={customSymbolPreview} 
                      alt="Symbol Preview" 
                      style={{
                        maxWidth: '100px', 
                        maxHeight: '100px', 
                        objectFit: 'contain',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '5px',
                        background: 'white'
                      }} 
                    />
                    <p style={{margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280'}}>
                      Symbol Selected ✅
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Financial Details */}
          <div className="form-section">
            <h4><i className="fas fa-money-bill-wave"></i> Financial Details</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="assets">Total Assets (₹)</label>
                <input
                  id="assets"
                  type="number"
                  name="assets"
                  value={formData.assets}
                  onChange={handleChange}
                  placeholder="10000000"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="liabilities">Total Liabilities (₹)</label>
                <input
                  id="liabilities"
                  type="number"
                  name="liabilities"
                  value={formData.liabilities}
                  onChange={handleChange}
                  placeholder="500000"
                  min="0"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="criminalCases">Criminal Cases</label>
              <input
                id="criminalCases"
                type="number"
                name="criminalCases"
                value={formData.criminalCases}
                onChange={handleChange}
                placeholder="0"
                min="0"
              />
            </div>
          </div>


          {/* ✅ NEW: Affidavit PDF Section */}
          <div className="form-section">
            <h4><i className="fas fa-file-pdf"></i> Affidavit PDF</h4>
            <div className="affidavit-upload-container">
              {affidavitPreview ? (
                <div className="affidavit-preview-wrapper">
                  <div className="affidavit-preview">
                    <i className="fas fa-file-pdf"></i>
                    <span>{affidavitPreview}</span>
                  </div>
                  <button
                    type="button"
                    className="remove-affidavit-btn"
                    onClick={removeAffidavit}
                    title="Remove affidavit"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <div className="affidavit-upload-box">
                  <input
                    type="file"
                    id="editAffidavitUpload"
                    accept="application/pdf"
                    onChange={handleAffidavitChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="editAffidavitUpload" className="affidavit-upload-label">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>Click to upload affidavit PDF</span>
                    <small>PDF only (Max 10MB)</small>
                  </label>
                </div>
              )}
            </div>
          </div>


          {/* Additional Information */}
          <div className="form-section">
            <h4><i className="fas fa-info-circle"></i> Additional Information</h4>
            <div className="form-group">
              <label htmlFor="backgroundSourceUrl">Background Source URL</label>
              <input
                id="backgroundSourceUrl"
                type="url"
                name="backgroundSourceUrl"
                value={formData.backgroundSourceUrl}
                onChange={handleChange}
                placeholder="https://myneta.info/candidate123"
              />
              <small className="field-hint">
                Source URL for candidate background (e.g., MyNeta, ADR)
              </small>
            </div>
          </div>


          <div className="modal-footer inline-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary" disabled={loading}>
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <div className="spinner-small"></div>
                  {t('common.updating') || 'Updating...'}
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  {t('common.update') || 'Update Candidate'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default EditCandidateModal;
